import { useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { isEqual } from 'lodash';
import { Store } from 'components/common/types/Store.types';
import { TagInclude } from 'components/common/types/Deal.types';
import { useCategoryOptions } from 'customHooks/useCategoryOptions';
import { canDisplayOptionResults } from 'components/pages/Planner/PlannerSecondaryPanel/utils';
import { isValueRequired } from 'components/pages/PCM/Products/utils/isValueRequired';
import SectionDivider from 'components/pages/Planner/SectionDivider';
import { hideSecondaryPanel } from 'store/productManagement/reducer';
import { CircularLoadingSpinner } from 'lib/CircularLoadingSpinner/CircularLoadingSpinner';
import TagsOptionsList from 'components/common/TagsOptionsList/TagsOptionsList';
import Search from 'lib/Search';
import SelectionPermissionDropdown from '../SelectionPermissionDropdown/SelectionPermissionDropdown';
import { permissionOptions } from '../../consts';
import { isPanelConfirmDisabled } from '../../utils/isPanelConfirmDisabled';
import { ProductTagsPanelProps } from './ProductTags.types';
import { secondaryPanelLayoutStyles } from '../../styles';
import { ProductSecondaryPanel } from '../ProductSecondaryPanel/ProductSecondaryPanel';

const EMPTY_TAGS: TagInclude[] = [];

const ProductTagsPanel: React.FC<ProductTagsPanelProps> = ({ onConfirm, isReadOnly = false }) => {
  const dispatch = useDispatch();
  const tagOptions = useSelector((state: Store) => state.userOptions.referenceOptions.tags);
  const isFetchingReferences = useSelector((state: Store) => state.dealManagement.isFetchingReferences);
  const defaultTags = useSelector((state: Store) => state.productManagement.newProduct.tags);

  const defaultSelectedPermission =
    permissionOptions.find(({ code }) => code === defaultTags?.selectionPermission) ?? permissionOptions[0];

  const [selectedPermission, setSelectedPermission] = useState(defaultSelectedPermission);

  const initialSelectedTags = useMemo(() => defaultTags?.value ?? EMPTY_TAGS, [defaultTags?.value]);

  const {
    onCategoryDirectionSort,
    searchText,
    openCategories,
    onFilterTextChange,
    selectedOptions: selectedTags,
    filteredOptions,
    getNumberOfFilteredOptions,
    sortDirection,
    includedOptions,
    excludedOptions,
    isOptionSelected,
    isOptionIncluded,
    onOptionIncludeExcludeChange,
    onToggleCategory,
    setSelectedOptions,
    onCategoryIncludeAll,
    onCategoryExcludeAll,
    getCategoryIncludeState,
  } = useCategoryOptions({ options: tagOptions, initialSelectedOptions: initialSelectedTags });

  const isChange = useMemo(() => !isEqual(initialSelectedTags, selectedTags), [initialSelectedTags, selectedTags]);

  const isDisabled =
    isReadOnly ||
    isPanelConfirmDisabled({
      field: defaultTags,
      selectedPermission,
      newFieldValue: selectedTags?.length ? selectedTags : undefined,
    });

  return (
    <ProductSecondaryPanel
      title="Tags"
      onSubmit={() => onConfirm({ value: selectedTags as TagInclude[], selectedPermission })}
      onCancel={() => dispatch(hideSecondaryPanel())}
      isDisabled={isDisabled}
      isChange={isChange}
      customStyles={secondaryPanelLayoutStyles}
    >
      <SelectionPermissionDropdown
        selectedPermission={selectedPermission}
        setSelectedPermission={setSelectedPermission}
        permissionOptions={permissionOptions}
      />

      {isValueRequired(selectedPermission.code) ? (
        <>
          <div className="my-4">
            <SectionDivider />
          </div>

          {isFetchingReferences ? (
            <CircularLoadingSpinner />
          ) : (
            <>
              <div className="mb-2">
                <Search value={searchText} placeholder="Search tags" onChange={onFilterTextChange} />
              </div>

              {canDisplayOptionResults(filteredOptions) ? (
                <TagsOptionsList
                  filteredOptions={filteredOptions}
                  selectedOptions={selectedTags}
                  openCategories={openCategories}
                  sortDirection={sortDirection}
                  includedOptions={includedOptions}
                  excludedOptions={excludedOptions}
                  onSort={onCategoryDirectionSort}
                  onToggleCategory={onToggleCategory}
                  setSelectedOptions={setSelectedOptions}
                  getNumberOfFilteredOptions={getNumberOfFilteredOptions}
                  isOptionSelected={isOptionSelected}
                  isOptionIncluded={isOptionIncluded}
                  onOptionIncludeExcludeChange={onOptionIncludeExcludeChange}
                  onCategoryIncludeAll={onCategoryIncludeAll}
                  onCategoryExcludeAll={onCategoryExcludeAll}
                  getCategoryIncludeState={getCategoryIncludeState}
                />
              ) : (
                <p>There are no results matching your search</p>
              )}
            </>
          )}
        </>
      ) : null}
    </ProductSecondaryPanel>
  );
};

export default ProductTagsPanel;
